import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Chip,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Paper,
  CircularProgress,
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Description as DescriptionIcon,
  Close as CloseIcon,
  ContactMail as ContactMailIcon,
} from '@mui/icons-material';
import { getImageUrl } from '../../utils/imageUtils';
import { format } from 'date-fns';
import { getEventById } from '../../services/eventService';
import { apiWithAuth } from '../../services/api';

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'pending':
      return 'warning';
    case 'accepted':
    case 'approved':
      return 'success';
    case 'waitlist':
      return 'info';
    case 'confirmed':
      return 'success';
    case 'rejected':
      return 'error';
    default:
      return 'default';
  }
};

const ApplicationCard = ({
  application,
  onAccept,
  onReject,
  showActions = true,
  isOrganizer = false,
}) => {
  const history = useHistory();
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const event = application.event || {};
  const imageSrc = event.image_url ? getImageUrl(event.image_url) : 'https://via.placeholder.com/600x300?text=Event';
  
  // Fetch full event details when dialog opens (includes table_tiers and fee)
  const { data: fullEvent, isLoading: eventLoading } = useQuery(
    ['event', event.event_id],
    () => getEventById(event.event_id),
    { enabled: paymentDialogOpen && !!event.event_id }
  );
  
  // Fetch organizer profile for contact info
  const { data: organizerProfile } = useQuery(
    ['organizerProfile', fullEvent?.organizer_id],
    async () => {
      if (!fullEvent?.organizer_id) return null;
      const token = localStorage.getItem('token');
      // We need to get organizer by ID - let's try to get it from the event's organizer
      // Since we don't have a direct endpoint, we'll use the organizer_id from the event
      // For now, we'll fetch all organizers and find the one we need, or we can add organizer info to event response
      try {
        const response = await apiWithAuth(token).get('/organizers/');
        const organizers = response.data;
        return organizers.find(org => org.organizer_id === fullEvent.organizer_id) || null;
      } catch (error) {
        console.error('Error fetching organizer:', error);
        return null;
      }
    },
    { enabled: paymentDialogOpen && !!fullEvent?.organizer_id }
  );
  
  // Use full event data if available, otherwise fall back to basic event data
  const eventData = fullEvent || event;
  
  // Calculate payment amount
  const getPaymentAmount = () => {
    if (!eventData) return 'Free';
    
    // Check for selected tier first
    if (eventData.table_tiers && application.selected_tier_id !== null && application.selected_tier_id !== undefined) {
      // Handle both string and number comparisons
      const selectedTier = eventData.table_tiers.find(tier => 
        tier.id === application.selected_tier_id || 
        tier.id === Number(application.selected_tier_id) ||
        String(tier.id) === String(application.selected_tier_id)
      );
      if (selectedTier && selectedTier.fee !== null && selectedTier.fee !== undefined) {
        const fee = parseFloat(selectedTier.fee);
        if (!isNaN(fee)) {
          return `$${fee.toFixed(2)}`;
        }
      }
    }
    
    // Fall back to single event fee
    if (eventData.fee !== null && eventData.fee !== undefined) {
      const fee = parseFloat(eventData.fee);
      if (!isNaN(fee) && fee > 0) {
        return `$${fee.toFixed(2)}`;
      }
    }
    
    return 'Free';
  };
  
  const paymentDeadline = application.payment_deadline ? new Date(application.payment_deadline) : null;
  const isOverdue = paymentDeadline && paymentDeadline < new Date() && application.payment_status !== 'confirmed';

  return (
    <Card
      onClick={() => {
        if (event.event_id) {
          history.push(`/event/${event.event_id}`);
        }
      }}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        cursor: event.event_id ? 'pointer' : 'default',
        transition: 'transform 160ms ease, box-shadow 160ms ease',
        '&:hover': {
          transform: event.event_id ? 'translateY(-2px)' : 'none',
          boxShadow: event.event_id ? '0 10px 24px rgba(0,0,0,0.12)' : 'inherit',
        },
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <CardMedia
          component="img"
          height="140"
          image={imageSrc}
          alt={event.title || 'Event'}
          sx={{ objectFit: 'cover' }}
        />
        <Chip
          label={(event.date && format(new Date(event.date), 'MMM d, yyyy')) || 'TBD'}
          size="small"
          sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(255,255,255,0.95)' }}
        />
      </Box>
      <CardContent sx={{ flexGrow: 1 }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6" gutterBottom>
              {event.title || 'Event Application'}
            </Typography>
            <Chip
              label={(() => {
                const status = application.status;
                // Normalize status display
                if (!status) return 'Pending';
                const statusLower = status.toLowerCase();
                if (statusLower === 'waitlist') return 'Waitlist';
                if (statusLower === 'confirmed' || statusLower === 'confirm-payment') return 'Confirmed';
                return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
              })()}
              color={(() => {
                // If has waitlist position, show as info
                if (application.waitlist_position) return 'info';
                return getStatusColor(application.status);
              })()}
              size="small"
              sx={{ mb: 2 }}
            />
            {application.waitlist_position && (
              <Typography variant="caption" color="info.main" sx={{ display: 'block', mt: 0.5 }}>
                Position #{application.waitlist_position}
              </Typography>
            )}
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Event Details
            </Typography>
            <Typography variant="body2">Date: {(event.date && new Date(event.date).toLocaleDateString()) || 'TBD'}</Typography>
            <Typography variant="body2">Location: {event.location || 'TBD'}</Typography>
          </Box>

          {/* Payment Status for Accepted Applications */}
          {(application.status?.toLowerCase() === 'accepted' || application.status?.toLowerCase() === 'approved') && 
           application.payment_status === 'pending' && (
            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                color="warning"
                fullWidth
                onClick={(e) => {
                  e.stopPropagation();
                  setPaymentDialogOpen(true);
                }}
                sx={{ mb: 1 }}
              >
                Complete Payment
              </Button>
              {application.payment_deadline && (
                <Typography variant="caption" color="text.secondary">
                  Payment due: {format(new Date(application.payment_deadline), 'MMM d, yyyy')}
                </Typography>
              )}
            </Box>
          )}

          {isOrganizer && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Vendor Details
              </Typography>
              <Typography variant="body2">
                Business: {application.vendor?.businessName}
              </Typography>
              <Typography variant="body2">
                Category: {application.vendor?.category}
              </Typography>
            </Box>
          )}

          {(application.notes || application.comments) && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Notes
              </Typography>
              <Typography variant="body2">
                {(() => {
                  // Remove "Proposal:" and "Price Quote:" labels from comments for vendor view
                  let cleanedNotes = application.notes || application.comments || '';
                  cleanedNotes = cleanedNotes.replace(/^Proposal:\s*/gim, '');
                  cleanedNotes = cleanedNotes.replace(/^Price Quote:\s*/gim, '');
                  // Clean up any extra newlines that might result
                  cleanedNotes = cleanedNotes.replace(/\n{3,}/g, '\n\n');
                  return cleanedNotes.trim();
                })()}
              </Typography>
            </Box>
          )}

          {showActions && application.status === 'pending' && isOrganizer && (
            <Stack direction="row" spacing={1} sx={{ mt: 'auto' }}>
              <Button
                variant="contained"
                color="success"
                fullWidth
                onClick={() => onAccept(application.id)}
              >
                Accept
              </Button>
              <Button
                variant="contained"
                color="error"
                fullWidth
                onClick={() => onReject(application.id)}
              >
                Reject
              </Button>
            </Stack>
          )}
        </Stack>
      </CardContent>

      {/* Payment Details Dialog */}
      <Dialog
        open={paymentDialogOpen}
        onClose={() => setPaymentDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Payment Information</Typography>
          <Button
            onClick={() => setPaymentDialogOpen(false)}
            sx={{ minWidth: 'auto', p: 1 }}
          >
            <CloseIcon />
          </Button>
        </DialogTitle>
        <DialogContent>
          {eventLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  {eventData.title || event.title || 'Event'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {eventData.date && format(new Date(eventData.date), 'EEEE, MMMM d, yyyy')} • {eventData.location || event.location}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

          {/* Payment Status Alert */}
          {isOverdue && (
            <Alert severity="error" icon={<WarningIcon />} sx={{ mb: 2 }}>
              <Typography variant="body2">
                Payment deadline has passed. Please contact the event organizer immediately.
              </Typography>
            </Alert>
          )}

          {!isOverdue && (
            <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
              <Typography variant="body2">
                Payment is required to secure your spot.
                {paymentDeadline && (
                  <span> Payment is due by <strong>{format(paymentDeadline, 'MMMM d, yyyy')}</strong></span>
                )}
              </Typography>
            </Alert>
          )}

          {/* Payment Details */}
          <Paper sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <MoneyIcon color="primary" />
              Payment Details
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <MoneyIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Amount Due"
                  secondary={
                    <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                      {getPaymentAmount()}
                    </Typography>
                  }
                />
              </ListItem>
              {paymentDeadline && (
                <ListItem>
                  <ListItemIcon>
                    <ScheduleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Payment Deadline"
                    secondary={format(paymentDeadline, 'EEEE, MMMM d, yyyy')}
                  />
                </ListItem>
              )}
              {application.selected_tier_id !== null && application.selected_tier_id !== undefined && eventData.table_tiers && (
                <ListItem>
                  <ListItemIcon>
                    <InfoIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Selected Tier"
                    secondary={
                      eventData.table_tiers.find(tier => 
                        tier.id === application.selected_tier_id || 
                        tier.id === Number(application.selected_tier_id) ||
                        String(tier.id) === String(application.selected_tier_id)
                      )?.name || 'N/A'
                    }
                  />
                </ListItem>
              )}
            </List>
          </Paper>

          {/* Payment Instructions */}
          {eventData.payment_instructions ? (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <DescriptionIcon color="primary" />
                Payment Instructions
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.8,
                  color: 'text.primary',
                }}
              >
                {eventData.payment_instructions}
              </Typography>
            </Paper>
          ) : (
            <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
              <Typography variant="body2">
                Payment instructions have not been provided yet. Please contact the event organizer for payment details.
              </Typography>
            </Alert>
          )}

          {/* Organizer Contact Information */}
          {organizerProfile?.contact_info && (
            <Paper sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
              <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ContactMailIcon color="primary" />
                Organizer Contact Information
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.8,
                  color: 'text.primary',
                }}
              >
                {organizerProfile.contact_info}
              </Typography>
              {organizerProfile.organization_name && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {organizerProfile.organization_name}
                </Typography>
              )}
            </Paper>
          )}

          {/* Policies */}
          {(eventData.cancellation_policy || eventData.refund_policy) && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Policies
              </Typography>
              <Divider sx={{ my: 1 }} />
              {eventData.cancellation_policy && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Cancellation Policy
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {eventData.cancellation_policy}
                  </Typography>
                </Box>
              )}
              {eventData.refund_policy && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Refund Policy
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {eventData.refund_policy}
                  </Typography>
                </Box>
              )}
            </Paper>
          )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPaymentDialogOpen(false)} variant="outlined">
            Close
          </Button>
          {eventData.event_id && (
            <Button
              onClick={() => {
                setPaymentDialogOpen(false);
                history.push(`/event/${eventData.event_id}`);
              }}
              variant="contained"
            >
              View Event Details
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default ApplicationCard; 